import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from './shell/AppShell';
import EmailBodyEditor from './EmailBodyEditor';
import DayScheduleEditor from './DayScheduleEditor';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import FilterSelect from './shell/FilterSelect';
import CandidatePreviewPanel from './CandidatePreviewPanel';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

const STEP_TYPES = [
  { key: 'email', label: 'Email', icon: '✉️', desc: 'Send an email via Outlook' },
  { key: 'sms', label: 'SMS', icon: '📱', desc: 'Send a text message via Twilio' },
  { key: 'wait', label: 'Wait', icon: '⏳', desc: 'Wait N days before next step' },
  { key: 'manual_task', label: 'Manual Task', icon: '✅', desc: 'Create a task for the recruiter' },
  { key: 'condition', label: 'Condition', icon: '🔀', desc: 'Branch based on reply status' },
];

// Step-type icons rendered in the candidate-widget header style: a tinted
// rounded square with an accent-colored icon. Colors match ColumnBasedLayout.
const STEP_ICON_PATHS = {
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  sms: 'M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.6 7.1L3 21l1.9-6.4A8 8 0 1121 12z',
  wait: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  manual_task: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  condition: 'M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4',
};
const StepIcon = ({ type, size = 22 }) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const accent = isDark ? '#5ec898' : '#047857';
  const tint = isDark ? '#24402f' : '#D1FAE5';
  const d = STEP_ICON_PATHS[type] || STEP_ICON_PATHS.email;
  return (
    <span className="flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, borderRadius: 6, backgroundColor: tint }}>
      <svg width={Math.round(size * 0.64)} height={Math.round(size * 0.64)} fill="none" stroke={accent} strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d={d} />
      </svg>
    </span>
  );
};

// Numbered badge in the same tinted-square style — used for step ordering.
const NumberIcon = ({ n, size = 22 }) => {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const accent = isDark ? '#5ec898' : '#047857';
  const tint = isDark ? '#24402f' : '#D1FAE5';
  return (
    <span className="flex items-center justify-center flex-shrink-0 font-semibold"
      style={{ width: size, height: size, borderRadius: 6, backgroundColor: tint, color: accent, fontSize: Math.round(size * 0.5) }}>
      {n}
    </span>
  );
};

const CampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'builder');

  // Keep the active tab in the URL (matches SearchDetailPage behavior).
  useEffect(() => {
    const cur = searchParams.get('tab');
    if (cur !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]);

  // Candidates tab: enrolled candidates + the linked searches they're in.
  const [rosterCandidates, setRosterCandidates] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  // Enrolled-tab search + filters (mirrors /searches page toolbar).
  const [rosterQuery, setRosterQuery] = useState('');
  const [rosterSearchFocused, setRosterSearchFocused] = useState(false);
  const [rosterStatusFilter, setRosterStatusFilter] = useState('all');
  const [rosterSearchFilter, setRosterSearchFilter] = useState('all'); // by linked search/project
  const [rosterSort, setRosterSort] = useState('enrolled_at');

  // Distinct linked searches across the roster, for the Search/Project filter.
  const rosterSearchOptions = useMemo(() => {
    const seen = new Map();
    let anyWithoutSearch = false;
    rosterCandidates.forEach(rc => {
      if (!rc.searches || rc.searches.length === 0) anyWithoutSearch = true;
      (rc.searches || []).forEach(s => {
        if (!seen.has(s.pipeline_id)) seen.set(s.pipeline_id, s.pipeline_name);
      });
    });
    return [
      { value: 'all', label: 'All Searches' },
      ...(anyWithoutSearch ? [{ value: 'none', label: 'No search' }] : []),
      ...[...seen.entries()].map(([id, name]) => ({ value: String(id), label: name })),
    ];
  }, [rosterCandidates]);

  const filteredRoster = useMemo(() => {
    let rows = [...rosterCandidates];
    if (rosterQuery.trim()) {
      const q = rosterQuery.toLowerCase();
      rows = rows.filter(rc =>
        (rc.candidate_name || '').toLowerCase().includes(q) ||
        (rc.candidate_company || '').toLowerCase().includes(q) ||
        (rc.candidate_title || '').toLowerCase().includes(q) ||
        (rc.candidate_email || '').toLowerCase().includes(q) ||
        (rc.searches || []).some(s => (s.pipeline_name || '').toLowerCase().includes(q))
      );
    }
    if (rosterStatusFilter !== 'all') {
      rows = rows.filter(rc => rc.sequence_status === rosterStatusFilter);
    }
    if (rosterSearchFilter !== 'all') {
      if (rosterSearchFilter === 'none') {
        rows = rows.filter(rc => !rc.searches || rc.searches.length === 0);
      } else {
        rows = rows.filter(rc => (rc.searches || []).some(s => String(s.pipeline_id) === rosterSearchFilter));
      }
    }
    rows.sort((a, b) => {
      if (rosterSort === 'name') return (a.candidate_name || '').localeCompare(b.candidate_name || '');
      if (rosterSort === 'company') return (a.candidate_company || '').localeCompare(b.candidate_company || '');
      if (rosterSort === 'emails') return (b.emails_sent || 0) - (a.emails_sent || 0);
      // default: enrolled_at (newest first)
      return new Date(b.enrolled_at || 0) - new Date(a.enrolled_at || 0);
    });
    return rows;
  }, [rosterCandidates, rosterQuery, rosterStatusFilter, rosterSearchFilter, rosterSort]);
  const [previewCandidateId, setPreviewCandidateId] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [rowMenuOpen, setRowMenuOpen] = useState(null); // sequence_id whose action menu is open
  const [selectedRoster, setSelectedRoster] = useState(new Set()); // selected sequence_ids
  const toggleRosterRow = (sequenceId) => setSelectedRoster(prev => {
    const next = new Set(prev);
    next.has(sequenceId) ? next.delete(sequenceId) : next.add(sequenceId);
    return next;
  });

  // Per-row sequence actions (mirror SequencesTab). Reload roster after each.
  const setSeqStatus = async (sequenceId, newStatus) => {
    try {
      await fetch(`${API}/api/outreach/sequences/${sequenceId}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus })
      });
      setRowMenuOpen(null);
      loadRosterCandidates();
    } catch (e) { console.error(e); }
  };
  const skipSeqStep = async (sequenceId) => {
    try {
      await fetch(`${API}/api/outreach/sequences/${sequenceId}/skip`, { method: 'POST', headers });
      setRowMenuOpen(null);
      loadRosterCandidates();
    } catch (e) { console.error(e); }
  };
  const removeSeq = async (sequenceId) => {
    if (!window.confirm('Remove this candidate from the campaign?')) return;
    try {
      await fetch(`${API}/api/outreach/sequences/${sequenceId}`, { method: 'DELETE', headers });
      setRowMenuOpen(null);
      loadRosterCandidates();
    } catch (e) { console.error(e); }
  };
  const [dirty, setDirty] = useState(false);
  const [mergeFields, setMergeFields] = useState([]);
  const [previewStep, setPreviewStep] = useState(null); // index of step being previewed

  // Sample data for merge field preview
  const sampleData = {
    first_name: 'Alex', last_name: 'Johnson', full_name: 'Alex Johnson',
    company: 'Acme Corp', title: 'VP of Engineering', location: 'New York, NY',
    search_name: campaign?.name || 'Software Engineering Leader',
    sender_name: JSON.parse(localStorage.getItem('user') || '{}')?.name || 'Jane Smith',
    sender_title: 'Managing Partner', firm_name: 'Banyak Group',
    time_of_day: new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening',
    tomorrow_or_monday: [5,6].includes(new Date().getDay()) ? 'Monday' : 'tomorrow',
    previous_day: 'yesterday'
  };

  const resolvePreview = (text) => {
    if (!text) return '';
    return text.replace(/\{\{(\w+)\}\}/g, (m, key) => sampleData[key] || m);
  };
  const [selectedStep, setSelectedStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [previewWidth, setPreviewWidth] = useState(null);
  const previewDragRef = useRef(false);

  // Per-step reveal state for the Gmail-style header (Cc/Bcc) + inline signature editor.
  const [ccOpen, setCcOpen] = useState({});
  const [bccOpen, setBccOpen] = useState({});

  // Scroll-spy: the middle column is a vertical stack of every step's editor.
  // Scrolling it highlights the matching step on the left (and drives the
  // preview on the right); clicking a step scrolls the middle column to it.
  const middleScrollRef = useRef(null);
  const stepRefs = useRef([]);
  const programmaticScrollRef = useRef(false);

  const goToStep = (index) => {
    setSelectedStep(index);
    const el = stepRefs.current[index];
    const container = middleScrollRef.current;
    if (el && container) {
      const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      programmaticScrollRef.current = true;
      container.scrollTo({ top: Math.max(0, top - 8), behavior: 'smooth' });
      window.clearTimeout(programmaticScrollRef.timer);
      programmaticScrollRef.timer = window.setTimeout(() => { programmaticScrollRef.current = false; }, 500);
    }
  };

  const onMiddleScroll = () => {
    if (programmaticScrollRef.current) return;
    const container = middleScrollRef.current;
    if (!container) return;
    const line = container.getBoundingClientRect().top + 100;
    let active = 0;
    for (let i = 0; i < stepRefs.current.length; i++) {
      const el = stepRefs.current[i];
      if (el && el.getBoundingClientRect().top <= line) active = i;
    }
    setSelectedStep(prev => (active !== prev ? active : prev));
  };

  // Draggable divider to resize the preview column (mirrors the Mapping tab).
  useEffect(() => {
    if (activeTab !== 'builder') return;
    const onMove = (e) => {
      if (!previewDragRef.current) return;
      const c = document.getElementById('builder-split');
      if (!c) return;
      const rect = c.getBoundingClientRect();
      const fromRight = rect.right - e.clientX;
      setPreviewWidth(Math.max(600, Math.min(rect.width * 0.7, fromRight)));
    };
    const onUp = () => { previewDragRef.current = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [activeTab]);
  const [analytics, setAnalytics] = useState(null);
  const [stepAnalytics, setStepAnalytics] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Connected senders and linked searches
  const [connectedSenders, setConnectedSenders] = useState([]);
  const [firmSettings, setFirmSettings] = useState(null);
  const [linkedSearches, setLinkedSearches] = useState([]);
  const [showSearchesPopover, setShowSearchesPopover] = useState(false);
  const [allSearches, setAllSearches] = useState([]);
  const [searchLinkQuery, setSearchLinkQuery] = useState('');
  const [signature, setSignature] = useState('');
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadCampaign = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setCampaign(data);
        setSteps(data.steps || []);
        // Auto-expand the schedule bar only for campaigns that haven't made ANY
        // scheduling decision yet (scheduling_enabled undefined). Once the user
        // has turned it on (with a schedule) OR explicitly left it off, collapse.
        // They can always re-open it. Runs once on load.
        const s = data.settings || {};
        const touchedOn = s.scheduling_enabled === true && s.schedule && Object.keys(s.schedule).length > 0;
        const touchedOff = s.scheduling_enabled === false;
        const undecided = !(touchedOn || touchedOff);
        setScheduleOpen(undecided);
        // Fresh campaign (no scheduling decision yet): default scheduling ON,
        // seeded with standard business hours (Mon–Fri 9–5), and open the bar.
        if (undecided) {
          const seed = {
            monday: { enabled: true, start: '09:00', end: '17:00' },
            tuesday: { enabled: true, start: '09:00', end: '17:00' },
            wednesday: { enabled: true, start: '09:00', end: '17:00' },
            thursday: { enabled: true, start: '09:00', end: '17:00' },
            friday: { enabled: true, start: '09:00', end: '17:00' },
            saturday: { enabled: false, start: '09:00', end: '17:00' },
            sunday: { enabled: false, start: '09:00', end: '17:00' },
          };
          const newSettings = { ...s, scheduling_enabled: true, schedule: seed, daily_send_limit: 200 };
          // Gap defaults to the org's global master gap (minutes → seconds).
          const orgGapMin = firmSettings?.master_gap_minutes;
          if (orgGapMin != null && s.stagger_seconds == null) newSettings.stagger_seconds = Math.round(orgGapMin * 60);
          setCampaign(prev => ({ ...prev, settings: newSettings }));
          // Persist so the seed sticks (fire-and-forget).
          fetch(`${API}/api/outreach/campaigns/${id}`, { method: 'PUT', headers, body: JSON.stringify({ settings: newSettings }) }).catch(() => {});
        }
      } else { navigate('/outreach'); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id]);


  const loadMergeFields = async () => {
    try {
      const res = await fetch(`${API}/api/outreach/merge-fields`, { headers });
      if (res.ok) setMergeFields(await res.json());
    } catch (e) {}
  };

  const loadConnectedSenders = async () => {
    try {
      const res = await fetch(`${API}/api/outreach/connected-senders`, { headers });
      if (res.ok) setConnectedSenders(await res.json());
    } catch (e) {}
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${id}/analytics`, { headers });
      if (res.ok) setAnalytics(await res.json());
    } catch (e) { console.error(e); }
    finally { setAnalyticsLoading(false); }
  };

  const loadLinkedSearches = async () => {
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${id}/searches`, { headers });
      if (res.ok) setLinkedSearches(await res.json());
    } catch (e) {}
  };

  const linkSearch = async (pipelineId) => {
    try {
      await fetch(`${API}/api/outreach/campaigns/${id}/searches`, { method: 'POST', headers, body: JSON.stringify({ pipeline_id: pipelineId }) });
      loadLinkedSearches();
      setSearchLinkQuery('');
    } catch (e) {}
  };

  const unlinkSearch = async (pipelineId) => {
    try {
      await fetch(`${API}/api/outreach/campaigns/${id}/searches/${pipelineId}`, { method: 'DELETE', headers });
      loadLinkedSearches();
    } catch (e) {}
  };

  // Settings helpers
  const settings = campaign?.settings || {};
  const updateSettings = (updates) => {
    const newSettings = { ...settings, ...updates };
    setCampaign(prev => ({ ...prev, settings: newSettings }));
    updateCampaign({ settings: newSettings });
  };
  const updateScheduleDay = (day, updates) => {
    // Seed a full week (defaults) the first time any day is edited, so the
    // saved schedule is always complete — never a partial object the backend
    // gate can't interpret. Weekends default off, weekdays 9–5.
    const DEFAULTS = {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' },
    };
    const base = (settings.schedule && Object.keys(settings.schedule).length) ? settings.schedule : DEFAULTS;
    const schedule = { ...DEFAULTS, ...base };
    schedule[day] = { ...(schedule[day] || { enabled: false, start: '09:00', end: '17:00' }), ...updates };
    updateSettings({ scheduling_enabled: true, schedule });
  };

  useEffect(() => { loadCampaign(); loadMergeFields(); loadConnectedSenders(); }, [loadCampaign]);
  useEffect(() => {
    fetch(`${API}/api/outreach/firm-settings`, { headers })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setFirmSettings(d); }).catch(() => {});
  }, []);
  useEffect(() => { if (showSettings) { loadLinkedSearches(); loadSignature(); } }, [showSettings]);
  useEffect(() => { if (activeTab === 'builder') { loadLinkedSearches(); loadSignature(); } }, [activeTab]);

  const loadSignature = async () => {
    setSignatureLoading(true);
    try {
      const res = await fetch(`${API}/api/outreach/signature`, { headers });
      if (res.ok) { const data = await res.json(); setSignature(data.signature || ''); }
    } catch (e) {}
    finally { setSignatureLoading(false); }
  };

  const saveSignature = async () => {
    setSignatureSaving(true);
    try {
      await fetch(`${API}/api/outreach/signature`, { method: 'PUT', headers, body: JSON.stringify({ signature }) });
    } catch (e) { console.error(e); }
    finally { setSignatureSaving(false); }
  };
  useEffect(() => { if (activeTab === 'analytics') loadAnalytics(); }, [activeTab]);
  useEffect(() => {
    if (activeTab !== 'builder') return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/outreach/campaigns/${id}/step-analytics`, { headers });
        if (r.ok) setStepAnalytics(await r.json());
      } catch (e) { /* non-fatal */ }
    })();
  }, [activeTab, id]);

  const loadRosterCandidates = useCallback(async () => {
    setRosterLoading(true);
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${id}/roster-candidates`, { headers });
      if (res.ok) setRosterCandidates(await res.json());
    } catch (e) { console.error(e); }
    finally { setRosterLoading(false); }
  }, [id]);

  useEffect(() => { if (activeTab === 'enrolled') loadRosterCandidates(); }, [activeTab, loadRosterCandidates]);

  const openPreview = (candidateId) => { setPreviewCandidateId(candidateId); setIsPreviewOpen(true); };

  // Save steps
  const saveSteps = async () => {
    // Before saving to a live campaign, check whether people are mid-sequence.
    // If so, confirm — edits apply to their remaining steps (never re-sends what
    // they already got). If a new trailing step was added and people already
    // completed, offer to send it to them too.
    try {
      const impRes = await fetch(`${API}/api/outreach/campaigns/${id}/edit-impact`, { headers });
      const imp = impRes.ok ? await impRes.json() : { in_progress: 0, completed: 0 };
      if (imp.in_progress > 0 || imp.completed > 0) {
        let msg = '';
        if (imp.in_progress > 0) {
          msg += `${imp.in_progress} ${imp.in_progress === 1 ? 'person is' : 'people are'} mid-campaign. Your changes apply to their remaining steps — no one is restarted, and no one is re-sent a step they already received.`;
        }
        if (!window.confirm((msg || 'Save changes to this campaign?') + '\n\nSave changes?')) {
          return;
        }
        // Offer to include already-completed people in a newly-added final step.
        if (imp.completed > 0) {
          const reactivate = window.confirm(
            `${imp.completed} ${imp.completed === 1 ? 'person has' : 'people have'} already completed this campaign.\n\n` +
            `If you added new step(s) at the end, do you want to also send those to them? ` +
            `(They'll only receive steps they haven't gotten yet — nothing is re-sent.)\n\n` +
            `OK = include them · Cancel = leave completed people as-is`
          );
          if (reactivate) window.__reactivateCompleted = true; else window.__reactivateCompleted = false;
        }
      }
    } catch (e) { /* impact check is best-effort; proceed with save */ }

    setSaving(true);
    try {
      const payload = steps.map(s => ({ step_type: s.step_type, config: s.config || {}, step_key: s.step_key }));
      const res = await fetch(`${API}/api/outreach/campaigns/${id}/steps`, {
        method: 'PUT', headers, body: JSON.stringify({ steps: payload })
      });
      if (res.ok) {
        setDirty(false);
        const saved = await res.json();
        setSteps(saved);
        // Optionally re-activate completed sequences to pick up new trailing steps.
        if (window.__reactivateCompleted) {
          window.__reactivateCompleted = false;
          try {
            const r = await fetch(`${API}/api/outreach/campaigns/${id}/reactivate-completed`, { method: 'POST', headers });
            if (r.ok) { const d = await r.json(); if (d.reactivated) alert(`${d.reactivated} completed ${d.reactivated === 1 ? 'person' : 'people'} will now receive the new step(s).`); }
          } catch (e) { console.error(e); }
        }
      }
    } catch (e) { console.error(e); alert('Failed to save steps'); }
    finally { setSaving(false); }
  };

  const updateCampaign = async (updates) => {
    try {
      const res = await fetch(`${API}/api/outreach/campaigns/${id}`, {
        method: 'PUT', headers, body: JSON.stringify(updates)
      });
      if (res.ok) setCampaign(prev => ({ ...prev, ...updates }));
    } catch (e) { console.error(e); }
  };

  // Step manipulation
  const addStep = (type) => {
    const defaults = {
      email: { config: { subject: '', body: '', reply_to_previous: false } },
      sms: { config: { body: '' } },
      wait: { config: { days: 3, business_days_only: true } },
      manual_task: { config: { title: '', description: '' } },
      condition: { config: { type: 'no_reply', days: 3, then_goto: null, else_action: 'stop' } },
    };
    const newStep = { step_type: type, ...defaults[type], position: steps.length };
    setSteps([...steps, newStep]);
    setSelectedStep(steps.length);
    setDirty(true);
  };

  const removeStep = (index) => {
    setSteps(steps.filter((_, i) => i !== index));
    setSelectedStep(prev => Math.max(0, prev >= index ? prev - 1 : prev));
    setDirty(true);
  };

  const moveStep = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
    setSteps(newSteps);
    setSelectedStep(target);
    setDirty(true);
  };

  // Drag-to-reorder the step list (smooth animated reflow via @hello-pangea/dnd,
  // same library as the candidate-profile widgets).
  const reorderSteps = (from, to) => {
    if (from == null || to == null || from === to) return;
    const newSteps = [...steps];
    const [moved] = newSteps.splice(from, 1);
    newSteps.splice(to, 0, moved);
    setSteps(newSteps);
    setSelectedStep(to);
    setDirty(true);
  };
  const handleStepDragEnd = (result) => {
    if (!result.destination) return;
    reorderSteps(result.source.index, result.destination.index);
  };

  const updateStepConfig = (index, configUpdates) => {
    setSteps(steps.map((s, i) => i === index ? { ...s, config: { ...s.config, ...configUpdates } } : s));
    setDirty(true);
  };
  const updateStep = (index, updates) => {
    setSteps(steps.map((s, i) => i === index ? { ...s, ...updates } : s));
    setDirty(true);
  };

  // Sequence actions

  const insertMergeField = (field, inputId) => {
    const input = document.getElementById(inputId);
    if (!input) return;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const value = input.value;
    const newValue = value.substring(0, start) + `{{${field}}}` + value.substring(end);
    input.value = newValue;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    // Trigger React state update
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
      || Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    if (nativeInputValueSetter) { nativeInputValueSetter.call(input, newValue); input.dispatchEvent(new Event('input', { bubbles: true })); }
  };


  if (loading) return <AppShell variant="recruiter"><div className="p-8 text-center text-gray-500">Loading campaign...</div></AppShell>;
  if (!campaign) return null;

  const CAMPAIGN_TABS = [
    { key: 'enrolled',   label: 'Enrolled',   count: rosterCandidates.length || undefined, iconPath: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
    { key: 'builder',    label: 'Campaign Builder', count: steps.length, iconPath: 'M4 6h16M4 12h16M4 18h7' },
    { key: 'analytics',  label: 'Analytics',  iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  ];

  const pageStrip = (
    <div className="h-10 flex items-center px-4 gap-1 border-b border-gray-200">
      <button onClick={() => navigate('/outreach')} className="flex-shrink-0 p-1 mr-1 hover:bg-gray-100 rounded text-gray-400" title="Back to campaigns">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
      </button>
      {CAMPAIGN_TABS.map(t => (
        <button
          key={t.key}
          onClick={() => setActiveTab(t.key)}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 -mb-px rounded-none transition-colors ${
            activeTab === t.key
              ? 'border-emerald-600 text-emerald-700 font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.iconPath} />
          </svg>
          {t.label}
          {t.count !== undefined && <span className="ml-0.5 text-gray-400 tabular-nums">{t.count}</span>}
        </button>
      ))}

      {/* Campaign context — inline title, code, status (matches SearchDetailPage) */}
      <div className="flex items-center gap-2 min-w-0 h-6 pl-3 ml-1 border-l border-gray-200">
        <h1 className="text-sm font-semibold text-gray-900 truncate min-w-0">{campaign.name}</h1>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${
          campaign.status === 'active' ? 'bg-green-100 text-green-700' :
          campaign.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
          campaign.status === 'archived' ? 'bg-red-100 text-red-600' :
          'bg-gray-100 text-gray-600'
        }`}>{campaign.status}</span>
        {campaign.description && <span className="text-[10px] text-gray-400 truncate">{campaign.description}</span>}
      </div>

      <div className="flex-1" />
    </div>
  );

  // Second header row (under the tabs) — search + filters on the left,
  // settings cog + status actions on the right. Matches SearchesPage's
  // stacked tab-row + toolbar-row pattern.
  const toolbarRow = (
    <div className="flex items-center gap-2 py-2 pl-4 bg-[color:var(--list-page-bg)] flex-wrap flex-shrink-0" style={{ paddingRight: activeTab === 'builder' ? '1rem' : 'calc(1rem + var(--sb-gutter, 15px))' }}>
      {/* Search + filters — only relevant on the Enrolled tab */}
      {activeTab === 'enrolled' && rosterCandidates.length > 0 && (
        <>
          <div className="relative">
            <input
              type="text"
              value={rosterQuery}
              onChange={(e) => setRosterQuery(e.target.value)}
              onFocus={() => setRosterSearchFocused(true)}
              onBlur={() => setRosterSearchFocused(false)}
              placeholder="Search candidates..."
              className="h-[30px] w-56 pl-7 pr-2 text-sm border border-gray-300 rounded focus:outline-none transition-shadow"
              style={rosterSearchFocused ? { boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.15), 0 0 14px 2px rgba(16, 185, 129, 0.22), 0 0 28px 3px rgba(16, 185, 129, 0.10)', borderColor: 'rgba(16, 185, 129, 0.4)' } : undefined}
            />
            <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <FilterSelect
            label="Status" value={rosterStatusFilter} defaultValue="all" onChange={setRosterStatusFilter}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'completed', label: 'Completed' },
            ]}
          />
          <FilterSelect
            label="Search" value={rosterSearchFilter} defaultValue="all" onChange={setRosterSearchFilter}
            minWidth={150} options={rosterSearchOptions}
          />
          <FilterSelect
            label="Sort" value={rosterSort} defaultValue="enrolled_at" onChange={setRosterSort}
            options={[
              { value: 'enrolled_at', label: 'Newest First' },
              { value: 'name', label: 'Name A-Z' },
              { value: 'company', label: 'Company A-Z' },
              { value: 'emails', label: 'Most Emails' },
            ]}
          />
          <span className="text-[11px] text-gray-400 tabular-nums">
            {filteredRoster.length}{filteredRoster.length !== rosterCandidates.length ? ` of ${rosterCandidates.length}` : ''}
          </span>
        </>
      )}

      {/* Steps-tab inline controls — Send From + tracking (mirror Settings) */}
      {activeTab === 'builder' && (
        <>
          <FilterSelect
            label="Send from"
            value={campaign.send_from_type || 'recruiter_outlook'}
            defaultValue="recruiter_outlook"
            onChange={(v) => { setCampaign(prev => ({ ...prev, send_from_type: v })); updateCampaign({ send_from_type: v }); }}
            options={[
              { value: 'recruiter_outlook', label: "Recruiter's Outlook" },
              { value: 'shared', label: 'Shared mailbox' },
            ]}
          />
          {campaign.send_from_type === 'shared' && (
            <input type="email" value={campaign.send_from_email || ''}
              onChange={(e) => setCampaign(prev => ({ ...prev, send_from_email: e.target.value }))}
              onBlur={() => updateCampaign({ send_from_email: campaign.send_from_email })}
              placeholder="shared@firm.com"
              className="h-[30px] px-2 text-sm border border-gray-300 rounded bg-white w-40 focus:outline-none focus:ring-1 focus:ring-emerald-400" />
          )}
          {/* Connected Searches — compact button + popover (mirrors Settings) */}
          <div className="relative">
            <button onClick={() => setShowSearchesPopover(v => !v)}
              className={`flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm rounded border transition-colors ${
                linkedSearches.length > 0 ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
              Searches{linkedSearches.length > 0 ? ` (${linkedSearches.length})` : ''}
            </button>
            {showSearchesPopover && (
              <>
                <div className="fixed inset-0 z-[9998]" onClick={() => setShowSearchesPopover(false)} />
                <div className="absolute left-0 top-[34px] z-[9999] w-72 bg-white rounded-lg border shadow-xl p-3 space-y-2">
                  <div className="text-xs font-semibold text-gray-900">Connected Searches</div>
                  <p className="text-[11px] text-gray-500">Link searches to auto-update pool statuses when outreach is sent.</p>
                  {linkedSearches.length > 0 && (
                    <div className="space-y-1">
                      {linkedSearches.map(s => (
                        <div key={s.pipeline_id} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs">
                          <span className="text-gray-800 truncate">{s.pipeline_name} <span className="text-gray-400">({s.enrolled_from_search})</span></span>
                          <button onClick={() => unlinkSearch(s.pipeline_id)} className="text-red-400 hover:text-red-600 text-[10px] flex-shrink-0 ml-2">Unlink</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input type="text" value={searchLinkQuery} onChange={(e) => {
                      setSearchLinkQuery(e.target.value);
                      if (e.target.value.length >= 2) {
                        fetch(`${API}/api/pipelines?search=${encodeURIComponent(e.target.value)}`, { headers })
                          .then(r => r.ok ? r.json() : []).then(setAllSearches).catch(() => {});
                      }
                    }}
                    placeholder="Search a pipeline to link..."
                    className="w-full px-2.5 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                  {searchLinkQuery.length >= 2 && allSearches.length > 0 && (
                    <div className="border rounded max-h-32 overflow-auto">
                      {allSearches.filter(s => !linkedSearches.some(ls => ls.pipeline_id === s.id)).map(s => (
                        <button key={s.id} onClick={() => linkSearch(s.id)}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-emerald-50 border-b last:border-0">{s.name}</button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <button onClick={() => updateSettings({ track_opens: settings.track_opens === false })}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm rounded border transition-colors ${
              settings.track_opens !== false ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            Track opens
          </button>
          <button onClick={() => updateSettings({ track_clicks: settings.track_clicks === false })}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm rounded border transition-colors ${
              settings.track_clicks !== false ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" /></svg>
            Track clicks
          </button>
        </>
      )}

      <div className="flex-1" />

      {/* Preview toggle — Campaign Builder tab only */}
      {activeTab === 'builder' && (
        <button onClick={() => setPreviewOpen(o => !o)}
          className={`flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-2.5 text-sm rounded border transition-colors ${
            previewOpen ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
          title={previewOpen ? 'Hide preview' : 'Show preview'}>
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {previewOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>}
          </svg>
          {previewOpen ? 'Hide preview' : 'Show preview'}
        </button>
      )}

      {/* Settings cog — next to Activate */}
      <button
        onClick={() => setShowSettings(true)}
        className="flex-shrink-0 h-[30px] w-[30px] flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
        title="Campaign settings"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {/* Status action buttons */}
      {campaign.status === 'draft' && steps.length > 0 && (
        <button onClick={() => updateCampaign({ status: 'active' })}
          className="flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          Activate
        </button>
      )}
      {campaign.status === 'active' && (
        <button onClick={() => updateCampaign({ status: 'paused' })}
          className="flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors">
          <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24"><path d="M6 5h4v14H6zm8 0h4v14h-4z" /></svg>
          Pause
        </button>
      )}
      {campaign.status === 'paused' && (
        <button onClick={() => updateCampaign({ status: 'active' })}
          className="flex-shrink-0 inline-flex items-center gap-1.5 h-[30px] px-3 text-sm font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          Resume
        </button>
      )}
    </div>
  );

  return (
    <AppShell variant="recruiter" pageStrip={pageStrip}>
      <div className="h-full flex overflow-hidden">
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
      {toolbarRow}
      {/* Content */}
      {activeTab === 'enrolled' ? (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-page-bg)' }}>
          {rosterLoading ? (
            <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : rosterCandidates.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              <p className="text-sm">No candidates enrolled yet.</p>
              <p className="text-xs mt-1">Add candidates to this campaign from a search or directly to see them here.</p>
            </div>
          ) : filteredRoster.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-sm">No candidates match your filters.</p>
              <button onClick={() => { setRosterQuery(''); setRosterStatusFilter('all'); setRosterSearchFilter('all'); }}
                className="text-xs mt-2 text-emerald-600 hover:text-emerald-700 font-medium">Clear filters</button>
            </div>
          ) : (
            <div className="flex-1 min-h-0 mx-3 mb-8 rounded-lg border shadow-sm flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-block-bg)' }}>
              <table className="w-full text-sm">
                <thead className="flex-shrink-0" style={{ backgroundColor: 'var(--list-header-bg)', color: 'var(--list-header-text)' }}>
                  <tr>
                    <th className="pl-4 pr-2 py-2 w-10 rounded-tl-lg" style={{ borderBottom: '1px solid var(--list-header-border)' }}>
                      <input
                        type="checkbox"
                        checked={filteredRoster.length > 0 && filteredRoster.every(rc => selectedRoster.has(rc.sequence_id))}
                        onChange={(e) => setSelectedRoster(e.target.checked ? new Set(filteredRoster.map(rc => rc.sequence_id)) : new Set())}
                        className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                    </th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Candidate</th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Searches / Projects</th>
                    <th className="text-left px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Status</th>
                    <th className="text-right px-3 py-2 text-[10px] font-medium uppercase tracking-wide" style={{ borderBottom: '1px solid var(--list-header-border)' }}>Emails</th>
                    <th className="px-2 py-2 w-10 rounded-tr-lg" style={{ borderBottom: '1px solid var(--list-header-border)' }}></th>
                  </tr>
                </thead>
                <tbody className="flex-1 overflow-y-auto">
                  {filteredRoster.map(rc => (
                    <tr
                      key={rc.sequence_id}
                      onClick={() => openPreview(rc.candidate_id)}
                      className={`border-b last:border-b-0 cursor-pointer transition-colors group ${selectedRoster.has(rc.sequence_id) ? 'bg-emerald-100/60' : previewCandidateId === rc.candidate_id ? 'bg-emerald-100' : 'bg-white/60 hover:bg-emerald-50/50'}`}
                    >
                          <td className="pl-4 pr-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedRoster.has(rc.sequence_id)}
                              onChange={() => toggleRosterRow(rc.sequence_id)}
                              className="w-4 h-4 rounded border-gray-300 accent-emerald-600 focus:ring-emerald-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2.5 min-w-0">
                              {rc.candidate_photo ? (
                                <img src={rc.candidate_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {(rc.candidate_name || '?').slice(0, 1).toUpperCase()}
                                </div>
                              )}
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{rc.candidate_name || 'Unknown'}</div>
                                <div className="text-[11px] text-gray-500 truncate">
                                  {[rc.candidate_title, rc.candidate_company].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {(rc.searches || []).length === 0 ? (
                                <span className="text-[10px] px-1.5 py-0.5 bg-gray-50 text-gray-400 rounded border border-gray-100 italic">No search</span>
                              ) : (
                                (rc.searches || []).map(s => (
                                  <button
                                    key={s.pipeline_id}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/search/${s.pipeline_id}`); }}
                                    className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors truncate max-w-[160px]"
                                    title={s.pipeline_name}
                                  >
                                    {s.pipeline_name}
                                  </button>
                                ))
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                              rc.sequence_status === 'active' ? 'bg-green-100 text-green-700' :
                              rc.sequence_status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                              rc.sequence_status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>{rc.sequence_status}</span>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-gray-600">{rc.emails_sent || 0}</td>
                          <td className="px-2 py-2.5 text-right relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setRowMenuOpen(rowMenuOpen === rc.sequence_id ? null : rc.sequence_id); }}
                              className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                              title="Actions"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 6a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4z" /></svg>
                            </button>
                            {rowMenuOpen === rc.sequence_id && (
                              <>
                                <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setRowMenuOpen(null); }} />
                                <div className="absolute right-2 top-9 z-20 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-xs text-left" onClick={(e) => e.stopPropagation()}>
                                  {rc.sequence_status === 'paused' ? (
                                    <button onClick={() => setSeqStatus(rc.sequence_id, 'active')} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700">Resume</button>
                                  ) : (
                                    <button onClick={() => setSeqStatus(rc.sequence_id, 'paused')} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700">Pause</button>
                                  )}
                                  <button onClick={() => skipSeqStep(rc.sequence_id)} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700">Skip to next step</button>
                                  <button onClick={() => { openPreview(rc.candidate_id); setRowMenuOpen(null); }} className="w-full text-left px-3 py-1.5 hover:bg-gray-50 text-gray-700">View candidate</button>
                                  <div className="my-1 border-t border-gray-100" />
                                  <button onClick={() => removeSeq(rc.sequence_id)} className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600">Remove from campaign</button>
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )}
        </div>
      ) : (
        <div className={`flex-1 min-h-0 ${activeTab === 'builder' ? 'overflow-hidden flex flex-col' : 'overflow-auto'}`} style={{ backgroundColor: 'var(--list-page-bg)', scrollbarGutter: activeTab === 'builder' ? 'auto' : 'stable' }}>
        <div className={activeTab === 'builder' ? 'px-4 pb-4 h-full flex flex-col' : 'max-w-5xl mx-auto px-4 py-6'}>

          {/* STEP BUILDER */}
          {activeTab === 'builder' && (
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Save bar */}
              {dirty && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center justify-between">
                  <span className="text-xs text-amber-700">You have unsaved changes</span>
                  <button onClick={saveSteps} disabled={saving}
                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Steps'}
                  </button>
                </div>
              )}

              <div id="builder-split" className="flex-1 min-h-0 flex overflow-hidden">
                  {/* MAIN: schedule (top) + steps & editor (row) — preview stays right */}
                  <div className="flex-1 min-w-0 flex flex-col min-h-0">
              {/* Full-width schedule bar (Option 1) — set schedule first, then build steps */}
              {(() => {
                const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
                const dayOf = (d) => settings.schedule?.[d] || { enabled: ['monday','tuesday','wednesday','thursday','friday'].includes(d), start: '09:00', end: '17:00' };
                const sel = dayOf(selectedDay);
                const limit = settings.daily_send_limit || 0;
                const gapMin = settings.stagger_seconds != null
                  ? +(settings.stagger_seconds / 60).toFixed(2)
                  : (firmSettings?.master_gap_minutes != null ? firmSettings.master_gap_minutes : '');
                const enabledDays = DAYS.filter(d => dayOf(d).enabled).map(d => d.slice(0,3));
                // Slots for a day, normalized to [{start,end}] (handles legacy single window).
                const slotsOf = (d) => {
                  const ds = dayOf(d);
                  if (!ds.enabled) return [];
                  return Array.isArray(ds.slots) && ds.slots.length ? ds.slots : [{ start: ds.start || '09:00', end: ds.end || '17:00' }];
                };
                const D3 = { monday:'Mon', tuesday:'Tue', wednesday:'Wed', thursday:'Thu', friday:'Fri', saturday:'Sat', sunday:'Sun' };
                const h12 = (t) => { const [H,M]=String(t||'').split(':').map(Number); const ap=H<12?'a':'p'; const h=H%12===0?12:H%12; return M?`${h}:${String(M).padStart(2,'0')}${ap}`:`${h}${ap}`; };
                const daySig = (d) => slotsOf(d).map(s => `${s.start}-${s.end}`).join(',');
                // Group consecutive enabled days with identical slots into ranges.
                const groupedText = (() => {
                  const parts = []; let i = 0;
                  while (i < DAYS.length) {
                    if (!dayOf(DAYS[i]).enabled) { i++; continue; }
                    const sig = daySig(DAYS[i]); let j = i;
                    while (j + 1 < DAYS.length && dayOf(DAYS[j+1]).enabled && daySig(DAYS[j+1]) === sig) j++;
                    const label = i === j ? D3[DAYS[i]] : `${D3[DAYS[i]]}–${D3[DAYS[j]]}`;
                    const times = slotsOf(DAYS[i]).map(s => `${h12(s.start)}–${h12(s.end)}`).join(', ');
                    parts.push(`${label} ${times}`); i = j + 1;
                  }
                  return parts.join(' · ') || 'no days';
                })();
                const copyToAll = () => {
                  const s = dayOf(selectedDay);
                  const srcSlots = Array.isArray(s.slots) && s.slots.length ? s.slots : [{ start: s.start || '09:00', end: s.end || '17:00' }];
                  const next = {};
                  DAYS.forEach(d => { const cur = dayOf(d); next[d] = { ...cur, slots: srcSlots.map(x => ({ ...x })), start: srcSlots[0].start, end: srcSlots[0].end }; });
                  updateSettings({ schedule: next });
                };
                return (
                  <div className="flex-shrink-0 mb-3 bg-white rounded shadow border border-gray-200 overflow-hidden">
                    {/* Header — icon/label + collapse (left) · "Send on a schedule" switch (right) */}
                    <div className="w-full flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                      <button
                        onClick={() => { if (settings.scheduling_enabled && !scheduleOpen) setScheduleOpen(true); }}
                        disabled={!settings.scheduling_enabled || scheduleOpen}
                        className={`flex items-center gap-2 flex-1 min-w-0 text-left rounded px-1 -mx-1 py-0.5 transition-colors group ${settings.scheduling_enabled && !scheduleOpen ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'}`}>
                        <StepIcon type="wait" size={18} />
                        <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Schedule</span>
                        {!scheduleOpen && (
                          settings.scheduling_enabled ? (
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {/* Grouped text — gets the room, truncates only when very long */}
                              <span className="text-[11px] text-gray-500 truncate min-w-0">{groupedText}</span>
                              {/* Metrics pinned right, outside the ellipsis */}
                              <span className="flex items-center gap-2 flex-shrink-0 text-[11px] text-gray-400">
                                {limit ? <span>{limit}/day</span> : null}
                                {gapMin ? <span>· {gapMin} min gap</span> : null}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[11px] text-gray-400 truncate">Anytime (no schedule)</span>
                          )
                        )}
                        {settings.scheduling_enabled && !scheduleOpen && (
                          <span className="ml-auto flex items-center gap-1 text-[10px] font-medium text-gray-400 group-hover:text-emerald-600 transition-colors flex-shrink-0">
                            Edit
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </span>
                        )}
                      </button>
                      <label className="flex items-center gap-2 flex-shrink-0 text-xs text-gray-500 cursor-pointer">
                        Send on a schedule
                        <button onClick={() => {
                            const on = !settings.scheduling_enabled;
                            if (on && (!settings.schedule || !Object.keys(settings.schedule).length)) {
                              const seed = {}; DAYS.forEach(d => seed[d] = dayOf(d)); updateSettings({ scheduling_enabled: true, schedule: seed });
                            } else updateSettings({ scheduling_enabled: on });
                            setScheduleOpen(on); // on → auto-open the schedule; off → collapse
                          }}
                          className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.scheduling_enabled ? 'bg-emerald-600' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.scheduling_enabled ? 'translate-x-4' : 'translate-x-0'}`} />
                        </button>
                      </label>
                    </div>

                    {scheduleOpen && (
                      <div className="p-3 space-y-2.5">
                        {settings.scheduling_enabled && (
                          <>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {DAYS.map(d => {
                                const ds = dayOf(d); const isSel = selectedDay === d;
                                return (
                                  <div key={d}
                                    className={`inline-flex items-center gap-1.5 h-[28px] pl-1.5 pr-2.5 rounded border transition-colors capitalize ${
                                      isSel && ds.enabled ? 'bg-emerald-600 border-emerald-600 text-white font-medium'
                                        : isSel ? 'bg-white border-emerald-400 ring-1 ring-emerald-300 text-gray-500 font-medium'
                                        : ds.enabled ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                                                     : 'bg-white border-gray-300 text-gray-400'
                                    }`}>
                                    <input type="checkbox" checked={ds.enabled}
                                      onChange={(e) => updateScheduleDay(d, { enabled: e.target.checked })}
                                      title={ds.enabled ? 'Sending on this day — uncheck to skip' : 'Off — check to send this day'}
                                      className={`w-3.5 h-3.5 rounded cursor-pointer ${isSel && ds.enabled ? 'accent-white' : 'accent-emerald-600'}`} />
                                    <button
                                      onClick={() => setSelectedDay(d)}
                                      title="Edit this day's hours"
                                      className="text-xs cursor-pointer">{d.charAt(0).toUpperCase() + d.slice(1)}</button>
                                  </div>
                                );
                              })}
                              <div className="flex-1" />
                              {sel.enabled && (
                                <button onClick={copyToAll} className="h-[28px] px-2 text-[11px] border border-gray-300 rounded text-gray-500 hover:bg-gray-50">Copy to all days</button>
                              )}
                            </div>

                            {/* Selected day's window(s) — draggable slots over an hour ruler */}
                            {sel.enabled ? (
                              <DayScheduleEditor
                                slots={Array.isArray(sel.slots) && sel.slots.length ? sel.slots : [{ start: sel.start || '09:00', end: sel.end || '17:00' }]}
                                onChange={(slots) => updateScheduleDay(selectedDay, { slots, start: slots[0]?.start, end: slots[0]?.end })}
                              />
                            ) : (
                              <div className="text-xs text-gray-400 capitalize py-2">{selectedDay.slice(0,3)} is off — click the pill above to enable it.</div>
                            )}

                            <div className="flex items-center gap-3 pt-2 border-t border-gray-100 flex-wrap">
                              <span className="text-xs text-gray-500 whitespace-nowrap">Daily limit</span>
                              <input type="range" min={0} max={1000} step={10} value={limit}
                                onChange={(e) => updateSettings({ daily_send_limit: parseInt(e.target.value) || null })}
                                className="accent-emerald-600" style={{ width: 220 }} />
                              <span className="text-sm font-medium whitespace-nowrap w-16">{limit || '∞'}<span className="text-[11px] text-gray-400"> /day</span></span>
                              <div className="w-px h-5 bg-gray-200" />
                              <span className="text-xs text-gray-500 whitespace-nowrap">Gap</span>
                              <input type="number" min={0} step="0.5" value={gapMin}
                                onChange={(e) => { const m = parseFloat(e.target.value); updateSettings({ stagger_seconds: m > 0 ? Math.round(m * 60) : null }); }}
                                placeholder="0" className="w-14 h-[28px] px-1.5 text-xs border border-gray-300 rounded" />
                              <span className="text-xs text-gray-400">min</span>
                              <div className="flex-1" />
                              <button onClick={() => setScheduleOpen(false)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium px-3.5 py-1.5 rounded border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 flex-shrink-0">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                Save &amp; collapse
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Builder: steps list (left) + editor (middle) + resizable preview (right) */}
                    <div className="flex-1 min-h-0 flex overflow-hidden">
                {/* LEFT: step list */}
                <div className="flex-shrink-0 overflow-y-auto min-h-0 pr-3" style={{ width: 380 }}>
                  <DragDropContext onDragEnd={handleStepDragEnd}>
                    <Droppable droppableId="steps-list">
                      {(dropProvided) => (
                        <div ref={dropProvided.innerRef} {...dropProvided.droppableProps} className="space-y-2">
                  {steps.map((step, index) => {
                    const typeInfo = STEP_TYPES.find(t => t.key === step.step_type) || STEP_TYPES[0];
                    const isSelected = selectedStep === index;
                    return (
                      <Draggable key={index} draggableId={`step-${index}`} index={index}>
                        {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        onClick={() => goToStep(index)}
                        className={`bg-white rounded shadow cursor-pointer transition-shadow ${
                          dragSnapshot.isDragging ? 'shadow-lg ring-1 ring-emerald-300' : ''
                        } ${isSelected && !dragSnapshot.isDragging ? 'ring-1 ring-emerald-300' : ''}`}>
                        {/* Delay connector — shows the wait before this step */}
                        {index > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1 text-[10px] text-gray-400 border-b border-dashed">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                            {(step.config?.delay_days ?? 0) > 0 ? `wait ${step.config.delay_days} day${step.config.delay_days === 1 ? '' : 's'}` : 'immediately after'}
                          </div>
                        )}
                        {/* Header — the drag handle (matches widget headers) */}
                        <div {...dragProvided.dragHandleProps}
                          className={`flex items-center justify-between px-3 py-2 border-b rounded-t cursor-grab active:cursor-grabbing transition-colors ${
                          isSelected ? 'bg-emerald-50' : 'bg-gray-50 hover:bg-gray-100/70'
                        }`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <NumberIcon n={index + 1} size={20} />
                            <h3 className="font-medium text-sm text-gray-900 truncate">{typeInfo.label}</h3>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                            <button onClick={(e) => { e.stopPropagation(); removeStep(index); }}
                              className="p-0.5 text-gray-300 hover:text-red-500" title="Delete step">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                            <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 24 24" title="Drag to reorder">
                              <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" /><circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" /><circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                            </svg>
                          </div>
                        </div>
                        {/* Below the header — smoothly expands/collapses with the active step.
                            Inline styles (not Tailwind arbitrary classes) so the animation is guaranteed to apply. */}
                        <div style={{ display: 'grid', gridTemplateRows: isSelected ? '1fr' : '0fr', transition: 'grid-template-rows 300ms ease' }}>
                          <div style={{ minHeight: 0, overflow: 'hidden', opacity: isSelected ? 1 : 0, transition: `opacity 200ms ease ${isSelected ? '75ms' : '0ms'}` }}>
                        {/* Body — summary */}
                        <div className="px-3 py-2">
                          {step.step_type === 'email' ? (
                            <div className="space-y-0.5">
                              <div className="text-[10px] text-gray-400 truncate">
                                <span className="text-gray-400">From:</span>{' '}
                                {(() => {
                                  const s = connectedSenders.find(cs => cs.id === step.config?.send_from_user_id);
                                  return s ? s.email : (campaign?.send_from_email || 'Campaign default');
                                })()}
                              </div>
                              <div className="text-[11px] text-gray-600 truncate">
                                <span className="text-gray-400">Subject:</span>{' '}
                                {step.config?.reply_to_previous ? 'Reply in thread' : (step.config?.subject || 'No subject')}
                              </div>
                            </div>
                          ) : (
                            <div className="text-[11px] text-gray-500 truncate">
                              {step.step_type === 'sms' && ((step.config?.body || '').replace(/<[^>]+>/g, '').substring(0, 40) || 'No message')}
                              {step.step_type === 'wait' && `Wait ${step.config?.days || 0} day${(step.config?.days || 0) !== 1 ? 's' : ''}`}
                              {step.step_type === 'manual_task' && ((step.config?.task_type === 'inmail' ? 'InMail · ' : '') + (step.config?.title || 'No title'))}
                              {step.step_type === 'condition' && `If no reply after ${step.config?.days || 3} days`}
                            </div>
                          )}
                        </div>
                        {/* Per-step analytics (email/sms only) */}
                        {(step.step_type === 'email' || step.step_type === 'sms') && (() => {
                          const a = stepAnalytics[index] || { messaged: 0, opened: 0, clicked: 0, replied: 0 };
                          const pct = (n) => a.messaged > 0 ? Math.round((n / a.messaged) * 100) : 0;
                          return (
                            <div className={`px-3 pb-2 pt-0.5 ${isSelected ? '' : ''}`}>
                              <div className="grid grid-cols-4 gap-1 text-center">
                                <div className="rounded bg-gray-50 py-1">
                                  <div className="text-[11px] font-semibold text-gray-700 leading-none">{a.messaged}</div>
                                  <div className="text-[8px] text-gray-400 uppercase tracking-wide mt-0.5">Sent</div>
                                </div>
                                <div className="rounded bg-gray-50 py-1">
                                  <div className="text-[11px] font-semibold text-gray-700 leading-none">{pct(a.opened)}%</div>
                                  <div className="text-[8px] text-gray-400 uppercase tracking-wide mt-0.5">Open</div>
                                </div>
                                {step.step_type === 'email' ? (
                                  <div className="rounded bg-gray-50 py-1">
                                    <div className="text-[11px] font-semibold text-gray-700 leading-none">{pct(a.clicked)}%</div>
                                    <div className="text-[8px] text-gray-400 uppercase tracking-wide mt-0.5">Click</div>
                                  </div>
                                ) : (
                                  <div className="rounded bg-gray-50 py-1">
                                    <div className="text-[11px] font-semibold text-gray-400 leading-none">–</div>
                                    <div className="text-[8px] text-gray-400 uppercase tracking-wide mt-0.5">Click</div>
                                  </div>
                                )}
                                <div className="rounded bg-emerald-50 py-1">
                                  <div className="text-[11px] font-semibold text-emerald-700 leading-none">{pct(a.replied)}%</div>
                                  <div className="text-[8px] text-emerald-600/70 uppercase tracking-wide mt-0.5">Reply</div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                          </div>
                        </div>
                      </div>
                        )}
                      </Draggable>
                    );
                  })}
                          {dropProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Add step buttons */}
                  <div className="pt-2 flex flex-col gap-1.5">
                    {STEP_TYPES.filter(type => type.key !== 'wait').map(type => (
                      <button key={type.key} onClick={() => { addStep(type.key); setSelectedStep(steps.length); }}
                        className="px-3 py-2 text-xs border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 flex items-center gap-1.5 transition-colors">
                        <span>{type.icon}</span>{type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* MIDDLE: scrollable stack of every step editor — scroll-spy highlights the left list and drives the preview */}
                <div className="min-w-0 flex-1 flex flex-col min-h-0">
                  {steps.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-lg border border-dashed">
                      <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                      <p className="text-sm text-gray-500">No steps yet. Add your first step from the left.</p>
                    </div>
                  ) : (
                  <div ref={middleScrollRef} onScroll={onMiddleScroll} className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
                  {steps.map((step, index) => {
                  const typeInfo = STEP_TYPES.find(t => t.key === step.step_type) || STEP_TYPES[0];
                  const isExpanded = selectedStep === index;

                  const delayDays = step.config?.delay_days ?? 0;
                  return (
                    <React.Fragment key={index}>
                    {/* Wait divider — the gap before this step, shown as you scroll */}
                    {index > 0 && (
                      <div className="flex items-center gap-2 px-6 select-none">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2.5 py-0.5">
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                          {delayDays > 0 ? `wait ${delayDays} day${delayDays === 1 ? '' : 's'}` : 'sends immediately after'}
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                    )}
                    <div ref={(el) => { stepRefs.current[index] = el; }}
                      onClick={() => setSelectedStep(index)}
                      className={`bg-white rounded shadow overflow-hidden flex flex-col transition-shadow ${isExpanded ? 'ring-1 ring-emerald-300' : ''}`}>
                      {/* Editor header — type dropdown + inline delay */}
                      <div className="flex-shrink-0 px-3 py-2 flex items-center gap-2 border-b bg-gray-50 flex-wrap">
                        <NumberIcon n={index + 1} size={20} />
                        <select value={step.step_type}
                          onChange={(e) => { updateStep(index, { step_type: e.target.value }); }}
                          className="h-[26px] text-xs border border-gray-300 rounded px-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400">
                          {STEP_TYPES.filter(t => t.key !== 'wait').map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                        {index === 0 ? (
                          <span className="inline-flex items-center gap-1 h-[26px] px-2 rounded text-xs bg-emerald-50 border border-emerald-200 text-emerald-700">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                            Send immediately
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                            Send
                            <input type="number" min={0} value={step.config?.delay_days ?? 0}
                              onChange={(e) => updateStepConfig(index, { delay_days: Math.max(0, parseInt(e.target.value) || 0) })}
                              className="w-12 h-[26px] text-center text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-400" />
                            {(step.config?.delay_days ?? 0) === 0 ? 'days later (immediately)' : `day${(step.config?.delay_days) === 1 ? '' : 's'} later`}
                          </span>
                        )}
                      </div>

                        <div className="px-4 py-3">
                          {/* EMAIL CONFIG */}
                          {step.step_type === 'email' && (
                            <div className="space-y-3">
                              {/* Envelope header — From / To / Cc / Bcc / Subject, consolidated (Option A) */}
                              {(() => {
                                const showCc = ccOpen[index] || !!step.config?.cc;
                                const showBcc = bccOpen[index] || !!step.config?.bcc;
                                const replying = !!step.config?.reply_to_previous;
                                return (
                              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                {/* From row */}
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 border-b border-gray-100 min-h-[38px]">
                                  <span className="text-[11px] text-gray-400 w-12 flex-shrink-0">From</span>
                                  {connectedSenders.length > 0 ? (
                                    <select value={step.config?.send_from_user_id || ''}
                                      onChange={(e) => updateStepConfig(index, { send_from_user_id: e.target.value ? parseInt(e.target.value) : null })}
                                      className="flex-1 min-w-[140px] text-[13px] bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400">
                                      <option value="">Campaign default</option>
                                      {connectedSenders.map(s => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}
                                    </select>
                                  ) : (
                                    <span className="flex-1 min-w-[140px] text-[13px] text-gray-500 truncate">{campaign?.send_from_email || 'Campaign default'}</span>
                                  )}
                                  {/* Cc / Bcc reveal links */}
                                  {!showCc && (
                                    <button type="button" onClick={() => setCcOpen(o => ({ ...o, [index]: true }))}
                                      className="flex-shrink-0 text-[12px] text-gray-400 hover:text-emerald-600 px-1">Cc</button>
                                  )}
                                  {!showBcc && (
                                    <button type="button" onClick={() => setBccOpen(o => ({ ...o, [index]: true }))}
                                      className="flex-shrink-0 text-[12px] text-gray-400 hover:text-emerald-600 px-1">Bcc</button>
                                  )}
                                  {/* Reply-in-thread toggle — always labeled pill, on/off obvious */}
                                  <button type="button"
                                    onClick={() => updateStepConfig(index, { reply_to_previous: !replying })}
                                    title={replying ? 'Sending as a reply inside the previous email thread — click to send as a new email instead' : 'Sending as a new email — click to reply inside the previous email thread'}
                                    className={`flex-shrink-0 inline-flex items-center gap-1.5 h-[24px] pl-1 pr-2.5 rounded-full text-[11px] font-medium border transition-colors ${replying ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-gray-300 text-gray-500 hover:border-emerald-300 hover:text-emerald-600'}`}>
                                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full ${replying ? 'bg-white/25' : 'bg-gray-100'}`}>
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>
                                    </span>
                                    Reply in thread
                                  </button>
                                </div>
                                {/* Cc row */}
                                {showCc && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 min-h-[34px] bg-gray-50/60">
                                    <span className="text-[11px] text-gray-400 w-12 flex-shrink-0">Cc</span>
                                    <input type="text" value={step.config?.cc || ''}
                                      onChange={(e) => updateStepConfig(index, { cc: e.target.value })}
                                      placeholder="colleague@firm.com, {{recruiter_email}}"
                                      className="flex-1 min-w-0 text-[13px] bg-transparent border-none focus:outline-none placeholder-gray-300" />
                                    <button type="button" title="Remove Cc"
                                      onClick={() => { updateStepConfig(index, { cc: '' }); setCcOpen(o => ({ ...o, [index]: false })); }}
                                      className="flex-shrink-0 text-gray-300 hover:text-gray-500"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                                  </div>
                                )}
                                {/* Bcc row */}
                                {showBcc && (
                                  <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-100 min-h-[34px] bg-gray-50/60">
                                    <span className="text-[11px] text-gray-400 w-12 flex-shrink-0">Bcc</span>
                                    <input type="text" value={step.config?.bcc || ''}
                                      onChange={(e) => updateStepConfig(index, { bcc: e.target.value })}
                                      placeholder="crm@firm.com, {{ats_bcc}}"
                                      className="flex-1 min-w-0 text-[13px] bg-transparent border-none focus:outline-none placeholder-gray-300" />
                                    <button type="button" title="Remove Bcc"
                                      onClick={() => { updateStepConfig(index, { bcc: '' }); setBccOpen(o => ({ ...o, [index]: false })); }}
                                      className="flex-shrink-0 text-gray-300 hover:text-gray-500"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                                  </div>
                                )}
                                {/* Subject row (labeled; hidden input when replying in-thread) */}
                                <div className="flex items-center gap-2 px-3 py-2">
                                  <span className="text-[11px] text-gray-400 w-12 flex-shrink-0">Subject</span>
                                  {!replying ? (
                                    <input id={`step-${index}-subject`} type="text" value={step.config?.subject || ''}
                                      onChange={(e) => updateStepConfig(index, { subject: e.target.value })}
                                      placeholder={`${step.config?.variant_b ? '(Variant A) ' : ''}e.g. Opportunity at {{firm_name}} — {{search_name}}`}
                                      className="flex-1 min-w-0 text-[14px] font-medium text-gray-900 bg-transparent border-none focus:outline-none placeholder-gray-300" />
                                  ) : (
                                    <span className="flex-1 text-[12px] text-gray-400 italic">Keeps the previous thread's subject (Re: …)</span>
                                  )}
                                </div>
                              </div>
                                );
                              })()}
                              <div>
                                <label className="text-xs font-medium text-gray-700 block mb-1">Body {step.config?.variant_b ? '(Variant A)' : ''}</label>
                                <EmailBodyEditor
                                  value={step.config?.body || ''}
                                  onChange={(html) => updateStepConfig(index, { body: html })}
                                  placeholder={`Hi {{first_name}},\n\nI wanted to reach out about {{search_name}}...`}
                                  minHeight={300}
                                />
                                {/* Variables — under the body */}
                                <div className="flex items-center gap-1 flex-wrap mt-2">
                                  <span className="text-[10px] text-gray-400 mr-0.5">Insert variable:</span>
                                  {mergeFields.map(f => (
                                    <button key={f.key} onClick={() => updateStepConfig(index, { body: (step.config?.body || '') + `{{${f.key}}}` })}
                                      className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded border border-emerald-200 hover:bg-emerald-100">
                                      {f.label}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* A/B Variant Toggle */}
                              <div className="border-t pt-3">
                                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                  <input type="checkbox" checked={!!step.config?.variant_b}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        updateStepConfig(index, { variant_b: { subject: step.config?.subject || '', body: step.config?.body || '' } });
                                      } else {
                                        const { variant_b, ...rest } = step.config || {};
                                        setSteps(steps.map((s, i) => i === index ? { ...s, config: rest } : s));
                                        setDirty(true);
                                      }
                                    }}
                                    className="rounded border-gray-300" />
                                  Enable A/B testing for this step
                                </label>
                              </div>

                              {/* Variant B */}
                              {step.config?.variant_b && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                                  <div className="text-xs font-semibold text-purple-700">Variant B</div>
                                  {!step.config?.reply_to_previous && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Subject (B)</label>
                                    <input type="text" value={step.config.variant_b.subject || ''}
                                      onChange={(e) => updateStepConfig(index, { variant_b: { ...step.config.variant_b, subject: e.target.value } })}
                                      className="w-full px-3 py-2 text-sm border rounded-lg" />
                                  </div>
                                  )}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Body (B)</label>
                                    <EmailBodyEditor
                                      value={step.config.variant_b.body || ''}
                                      onChange={(html) => updateStepConfig(index, { variant_b: { ...step.config.variant_b, body: html } })}
                                      placeholder={`Hi {{first_name}},\n\nA different angle to test against Variant A...`}
                                      minHeight={220}
                                    />
                                    {/* Variables — under Variant B body */}
                                    <div className="flex items-center gap-1 flex-wrap mt-2">
                                      <span className="text-[10px] text-gray-400 mr-0.5">Insert variable:</span>
                                      {mergeFields.map(f => (
                                        <button key={f.key} onClick={() => updateStepConfig(index, { variant_b: { ...step.config.variant_b, body: (step.config.variant_b.body || '') + `{{${f.key}}}` } })}
                                          className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded border border-purple-200 hover:bg-purple-200">
                                          {f.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Signature — attached at the bottom of the email step: toggle inclusion + edit/save the shared default */}
                              {(() => {
                                const sigOn = step.config?.include_signature !== undefined ? step.config.include_signature : !step.config?.reply_to_previous;
                                return (
                                  <div className="border-t pt-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                        <input type="checkbox" checked={sigOn}
                                          onChange={(e) => updateStepConfig(index, { include_signature: e.target.checked })}
                                          className="rounded border-gray-300" />
                                        Include email signature
                                      </label>
                                      <span className="text-[10px] text-gray-400">Default signature · shared across all campaigns</span>
                                    </div>
                                    <div className={`rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-2 transition-opacity ${sigOn ? '' : 'opacity-50'}`}>
                                      {signatureLoading ? (
                                        <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600 mx-auto" /></div>
                                      ) : (
                                        <>
                                          <EmailBodyEditor value={signature} onChange={setSignature} placeholder={"Best regards,\n{{sender_name}}\n{{sender_title}}, {{firm_name}}"} minHeight={150} />
                                          <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-gray-400">Editing this updates your signature everywhere.</span>
                                            <button type="button" onClick={saveSignature} disabled={signatureSaving}
                                              className="px-2.5 py-1 text-[11px] bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                                              {signatureSaving ? 'Saving…' : 'Save signature'}
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* SMS CONFIG */}
                          {step.step_type === 'sms' && (
                            <div className="space-y-3">
                              <div>
                                <div className="flex items-center justify-between mb-1">
                                  <label className="text-xs font-medium text-gray-700">Message (160 chars for single SMS)</label>
                                  <span className={`text-[10px] font-mono ${(step.config?.body || '').length > 160 ? 'text-amber-600' : 'text-gray-400'}`}>
                                    {(step.config?.body || '').length}/160
                                  </span>
                                </div>
                                <textarea value={step.config?.body || ''}
                                  onChange={(e) => updateStepConfig(index, { body: e.target.value })}
                                  placeholder={`Hi {{first_name}}, this is {{sender_name}} from {{firm_name}}. I'd love to connect about an opportunity. Are you available for a quick call?`}
                                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
                                  rows={4} />
                                {/* Variables — under the message */}
                                <div className="flex items-center gap-1 flex-wrap mt-2">
                                  <span className="text-[10px] text-gray-400 mr-0.5">Insert variable:</span>
                                  {mergeFields.filter(f => ['first_name', 'company', 'sender_name', 'firm_name', 'search_name'].includes(f.key)).map(f => (
                                    <button key={f.key} onClick={() => updateStepConfig(index, { body: (step.config?.body || '') + `{{${f.key}}}` })}
                                      className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded border border-emerald-200 hover:bg-emerald-100">
                                      {f.label}
                                    </button>
                                  ))}
                                </div>
                                {(step.config?.body || '').length > 160 && (
                                  <p className="text-[10px] text-amber-600 mt-1">Message exceeds 160 characters — will be sent as multiple segments (higher cost).</p>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-400">SMS is sent to the candidate's phone number. Requires Twilio configuration and A2P 10DLC registration for US numbers.</p>
                            </div>
                          )}

                          {/* WAIT CONFIG */}
                          {step.step_type === 'wait' && (
                            <div className="flex items-center gap-3">
                              <label className="text-xs font-medium text-gray-700">Wait</label>
                              <input type="number" min={1} max={90} value={step.config?.days || 3}
                                onChange={(e) => updateStepConfig(index, { days: parseInt(e.target.value) || 1 })}
                                className="w-20 px-2 py-1.5 text-sm border rounded-lg" />
                              <span className="text-xs text-gray-500">days before next step</span>
                              <label className="flex items-center gap-2 text-xs text-gray-600 ml-4">
                                <input type="checkbox" checked={step.config?.business_days_only !== false}
                                  onChange={(e) => updateStepConfig(index, { business_days_only: e.target.checked })}
                                  className="rounded border-gray-300" />
                                Business days only
                              </label>
                            </div>
                          )}

                          {/* MANUAL TASK CONFIG */}
                          {step.step_type === 'manual_task' && (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Task Title</label>
                                <input type="text" value={step.config?.title || ''}
                                  onChange={(e) => updateStepConfig(index, { title: e.target.value })}
                                  placeholder="e.g. Call candidate, Send LinkedIn InMail"
                                  className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Description (optional)</label>
                                <textarea value={step.config?.description || ''}
                                  onChange={(e) => updateStepConfig(index, { description: e.target.value })}
                                  placeholder="Any notes or instructions for the recruiter..."
                                  className="w-full px-3 py-2 text-sm border rounded-lg resize-none" rows={3} />
                              </div>
                            </div>
                          )}

                          {/* CONDITION CONFIG */}
                          {step.step_type === 'condition' && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-medium text-gray-700">If</span>
                                <select value={step.config?.type || 'no_reply'}
                                  onChange={(e) => updateStepConfig(index, { type: e.target.value })}
                                  className="px-2 py-1.5 border rounded-lg text-sm">
                                  <option value="no_reply">no reply received</option>
                                </select>
                                <span className="text-gray-500">after</span>
                                <input type="number" min={1} max={30} value={step.config?.days || 3}
                                  onChange={(e) => updateStepConfig(index, { days: parseInt(e.target.value) || 1 })}
                                  className="w-16 px-2 py-1.5 border rounded-lg text-sm" />
                                <span className="text-gray-500">days</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-medium text-gray-700">Then:</span>
                                <span className="text-gray-500">continue to next step</span>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="font-medium text-gray-700">Else (replied):</span>
                                <select value={step.config?.else_action || 'stop'}
                                  onChange={(e) => updateStepConfig(index, { else_action: e.target.value })}
                                  className="px-2 py-1.5 border rounded-lg text-sm">
                                  <option value="stop">Stop sequence (mark as replied)</option>
                                  <option value="continue">Continue to next step anyway</option>
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                    </React.Fragment>
                  );
                })}
                  </div>
                  )}
                </div>

                    </div>
                  </div>
                {/* RIGHT: message preview — draggable, toggled from the toolbar */}
                {previewOpen && (
                  <>
                    <div
                      onMouseDown={() => { previewDragRef.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; }}
                      className="flex-shrink-0 cursor-col-resize flex items-center justify-center group"
                      style={{ width: 16 }}
                      title="Drag to resize">
                      <div className="w-1 h-10 rounded-full bg-gray-300 group-hover:bg-gray-400 transition-colors" />
                    </div>
                    <div className="flex-shrink-0 min-w-0 flex flex-col min-h-0" style={{ width: previewWidth || 600 }}>
                  {steps.length > 0 && steps[selectedStep] && (() => {
                    const step = steps[selectedStep];
                    const cfg = step.config || {};
                    if (step.step_type === 'email') {
                      return (
                        <div className="bg-white rounded shadow overflow-hidden flex flex-col flex-1 min-h-0">
                          <div className="flex-shrink-0 px-3 py-2 border-b bg-gray-50 flex items-center gap-2"><StepIcon type={step.step_type} size={20} /><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Preview</span></div>
                          <div className="p-4 space-y-2 overflow-y-auto min-h-0 flex-1">
                            <div className="text-[10px] text-gray-400 uppercase tracking-wide">Subject</div>
                            <div className="text-sm font-medium text-gray-900">{resolvePreview(cfg.subject || '') || <span className="text-gray-300">No subject</span>}</div>
                            <div className="border-t my-2" />
                            <div className="email-preview-body text-sm text-gray-700 leading-relaxed" style={{ wordBreak: 'break-word' }}
                              dangerouslySetInnerHTML={{ __html: resolvePreview(cfg.body || '') || '<span style="color:#d1d5db">No message body</span>' }} />
                            <style>{`
                              .email-preview-body ul { list-style: disc; margin: 0.5em 0; padding-left: 1.5em; }
                              .email-preview-body ol { list-style: decimal; margin: 0.5em 0; padding-left: 1.5em; }
                              .email-preview-body li { margin: 0.15em 0; }
                              .email-preview-body a { color: #059669; text-decoration: underline; }
                              .email-preview-body p { margin: 0.4em 0; }
                              .email-preview-body strong { font-weight: 600; }
                              .email-preview-body em { font-style: italic; }
                              .email-preview-body u { text-decoration: underline; }
                            `}</style>
                          </div>
                        </div>
                      );
                    }
                    if (step.step_type === 'sms') {
                      return (
                        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                          <div className="flex-shrink-0 px-3 py-2 border-b bg-gray-50 flex items-center gap-2"><StepIcon type={step.step_type} size={20} /><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Preview</span></div>
                          <div className="p-4">
                            <div className="bg-gray-900 rounded-2xl p-3 max-w-[280px]">
                              <div className="bg-emerald-600 text-white text-sm rounded-2xl rounded-br-sm px-3 py-2 whitespace-pre-wrap" style={{ wordBreak: 'break-word' }}>
                                {resolvePreview(cfg.body || '') || 'No message'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (step.step_type === 'manual_task' && cfg.task_type === 'inmail') {
                      return (
                        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                          <div className="flex-shrink-0 px-3 py-2 border-b bg-gray-50 text-[11px] font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 110-4.13 2.06 2.06 0 010 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>
                            InMail Preview
                          </div>
                          <div className="p-4 space-y-2">
                            <div className="text-[10px] text-gray-400 uppercase tracking-wide">Subject</div>
                            <div className="text-sm font-medium text-gray-900">{resolvePreview(cfg.subject || '') || <span className="text-gray-300">No subject</span>}</div>
                            <div className="border-t my-2" />
                            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" style={{ wordBreak: 'break-word' }}>
                              {resolvePreview(cfg.body || '') || <span className="text-gray-300">No message</span>}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    // Non-message steps (wait, generic task, condition)
                    return (
                      <div className="bg-white rounded-lg border border-dashed shadow-sm p-6 text-center">
                        <p className="text-xs text-gray-400">No message preview for this step type.</p>
                      </div>
                    );
                  })()}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          {/* ANALYTICS */}
          {activeTab === 'analytics' && (
            <div>
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
              ) : !analytics ? (
                <div className="bg-white rounded-lg border p-8 text-center text-sm text-gray-500">No analytics data available</div>
              ) : (() => {
                // Aggregate overall stats
                const agg = {};
                for (const r of analytics.overall) {
                  const key = r.activity_type;
                  if (!agg[key]) agg[key] = { total: 0, A: 0, B: 0 };
                  agg[key].total += r.count;
                  if (r.variant === 'A') agg[key].A += r.count;
                  else if (r.variant === 'B') agg[key].B += r.count;
                }
                const sent = agg.email_sent?.total || 0;
                const opened = agg.email_opened?.total || 0;
                const clicked = agg.link_clicked?.total || 0;
                const replied = agg.email_replied?.total || 0;
                const skipped = agg.email_skipped?.total || 0;
                const failed = agg.email_failed?.total || 0;
                const smsSent = agg.sms_sent?.total || 0;
                const smsFailed = agg.sms_failed?.total || 0;
                const openRate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
                const clickRate = sent > 0 ? Math.round((clicked / sent) * 100) : 0;
                const replyRate = sent > 0 ? Math.round((replied / sent) * 100) : 0;
                const hasAB = (agg.email_sent?.A || 0) > 0 && (agg.email_sent?.B || 0) > 0;
                const hasSmS = smsSent > 0 || smsFailed > 0;

                return (
                  <div className="space-y-6">
                    {/* Summary cards */}
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {[
                        { label: 'Emails', value: sent, color: 'text-blue-600' },
                        { label: 'Opened', value: `${opened} (${openRate}%)`, color: 'text-indigo-600' },
                        { label: 'Clicked', value: `${clicked} (${clickRate}%)`, color: 'text-purple-600' },
                        { label: 'Replied', value: `${replied} (${replyRate}%)`, color: 'text-green-600' },
                        ...(hasSmS ? [{ label: 'SMS Sent', value: smsSent, color: 'text-teal-600' }] : []),
                        { label: 'Skipped', value: skipped + (agg.sms_skipped?.total || 0), color: 'text-yellow-600' },
                        { label: 'Failed', value: failed + smsFailed, color: (failed + smsFailed) > 0 ? 'text-red-600' : 'text-gray-400' },
                      ].map(s => (
                        <div key={s.label} className="bg-white rounded-lg border p-3 text-center">
                          <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
                          <div className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    {/* A/B comparison */}
                    {hasAB && (() => {
                      const aSent = agg.email_sent?.A || 0;
                      const bSent = agg.email_sent?.B || 0;
                      const metrics = [
                        { label: 'Sent', key: 'email_sent', isBase: true },
                        { label: 'Opened', key: 'email_opened' },
                        { label: 'Clicked', key: 'link_clicked' },
                        { label: 'Replied', key: 'email_replied' },
                      ];
                      const getRate = (count, base) => base > 0 ? Math.round((count / base) * 100) : 0;

                      return (
                        <div className="bg-white rounded-lg border shadow-sm p-5">
                          <h3 className="text-sm font-semibold text-gray-900 mb-4">A/B Test Results</h3>
                          <div className="space-y-4">
                            {metrics.map(m => {
                              const aVal = agg[m.key]?.A || 0;
                              const bVal = agg[m.key]?.B || 0;
                              const aRate = m.isBase ? null : getRate(aVal, aSent);
                              const bRate = m.isBase ? null : getRate(bVal, bSent);
                              const maxVal = Math.max(aVal, bVal, 1);
                              const aWins = !m.isBase && aRate > bRate && aVal > 0;
                              const bWins = !m.isBase && bRate > aRate && bVal > 0;
                              const tied = !m.isBase && aRate === bRate;

                              return (
                                <div key={m.key}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-medium text-gray-700">{m.label}</span>
                                    {!m.isBase && (aWins || bWins) && (
                                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${aWins ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                        {aWins ? 'A wins' : 'B wins'} {aWins ? `+${aRate - bRate}pp` : `+${bRate - aRate}pp`}
                                      </span>
                                    )}
                                    {tied && aVal > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Tied</span>}
                                  </div>
                                  {/* Variant A bar */}
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[10px] text-blue-600 font-medium w-3">A</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                                      <div className={`h-full rounded-full transition-all ${aWins ? 'bg-blue-500' : 'bg-blue-300'}`} style={{ width: `${Math.max((aVal / maxVal) * 100, aVal > 0 ? 8 : 0)}%` }} />
                                      <span className="absolute inset-y-0 left-2 flex items-center text-[10px] font-medium text-gray-700">{aVal}{aRate !== null ? ` (${aRate}%)` : ''}</span>
                                    </div>
                                  </div>
                                  {/* Variant B bar */}
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-purple-600 font-medium w-3">B</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                                      <div className={`h-full rounded-full transition-all ${bWins ? 'bg-purple-500' : 'bg-purple-300'}`} style={{ width: `${Math.max((bVal / maxVal) * 100, bVal > 0 ? 8 : 0)}%` }} />
                                      <span className="absolute inset-y-0 left-2 flex items-center text-[10px] font-medium text-gray-700">{bVal}{bRate !== null ? ` (${bRate}%)` : ''}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Overall winner */}
                          {(() => {
                            const aReplyRate = getRate(agg.email_replied?.A || 0, aSent);
                            const bReplyRate = getRate(agg.email_replied?.B || 0, bSent);
                            const aOpenRate = getRate(agg.email_opened?.A || 0, aSent);
                            const bOpenRate = getRate(agg.email_opened?.B || 0, bSent);
                            // Winner by reply rate first, then open rate
                            const winner = aReplyRate !== bReplyRate ? (aReplyRate > bReplyRate ? 'A' : 'B') : 
                                           aOpenRate !== bOpenRate ? (aOpenRate > bOpenRate ? 'A' : 'B') : null;
                            if (!winner || (aSent < 5 && bSent < 5)) return null;
                            return (
                              <div className={`mt-4 pt-3 border-t text-center text-xs ${winner === 'A' ? 'text-blue-700' : 'text-purple-700'}`}>
                                <span className="font-semibold">Variant {winner}</span> is performing better
                                {(aSent < 10 || bSent < 10) && <span className="text-gray-400 ml-1">(small sample — results may change)</span>}
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}

                    {/* Per-step funnel */}
                    {analytics.per_step.length > 0 && (
                      <div className="bg-white rounded-lg border shadow-sm p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Per-Step Breakdown</h3>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b">
                              <th className="py-2 text-left text-gray-500 font-medium">Step</th>
                              <th className="py-2 text-center text-gray-500 font-medium">Sent</th>
                              <th className="py-2 text-center text-gray-500 font-medium">Opened</th>
                              <th className="py-2 text-center text-gray-500 font-medium">Clicked</th>
                              <th className="py-2 text-center text-gray-500 font-medium">Replied</th>
                              <th className="py-2 text-center text-gray-500 font-medium">Skipped</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const stepAgg = {};
                              for (const r of analytics.per_step) {
                                const k = r.step_position;
                                if (!stepAgg[k]) stepAgg[k] = { position: k, type: r.step_type };
                                stepAgg[k][r.activity_type] = (stepAgg[k][r.activity_type] || 0) + r.count;
                                if (r.variant) {
                                  const vKey = `${r.activity_type}_${r.variant}`;
                                  stepAgg[k][vKey] = (stepAgg[k][vKey] || 0) + r.count;
                                }
                              }
                              const CellVal = ({ total, aVal, bVal }) => (
                                <td className="py-2 text-center">
                                  <span>{total}</span>
                                  {hasAB && (aVal > 0 || bVal > 0) && (
                                    <div className="text-[9px] text-gray-400 mt-0.5">
                                      <span className="text-blue-500">A:{aVal}</span>{' '}
                                      <span className="text-purple-500">B:{bVal}</span>
                                    </div>
                                  )}
                                </td>
                              );
                              return Object.values(stepAgg).sort((a, b) => a.position - b.position).map(s => (
                                <tr key={s.position} className="border-b border-gray-100">
                                  <td className="py-2 text-gray-700">Step {s.position + 1} <span className="text-gray-400">({s.type})</span></td>
                                  <CellVal total={(s.email_sent || 0) + (s.sms_sent || 0)} aVal={s.email_sent_A || 0} bVal={s.email_sent_B || 0} />
                                  <CellVal total={s.email_opened || 0} aVal={s.email_opened_A || 0} bVal={s.email_opened_B || 0} />
                                  <CellVal total={s.link_clicked || 0} aVal={s.link_clicked_A || 0} bVal={s.link_clicked_B || 0} />
                                  <CellVal total={s.email_replied || 0} aVal={s.email_replied_A || 0} bVal={s.email_replied_B || 0} />
                                  <td className="py-2 text-center">{s.email_skipped || 0}</td>
                                </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Daily activity */}
                    {analytics.daily.length > 0 && (
                      <div className="bg-white rounded-lg border shadow-sm p-5">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Daily Activity (Last 30 Days)</h3>
                        <div className="max-h-48 overflow-auto">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-white">
                              <tr className="border-b">
                                <th className="py-1.5 text-left text-gray-500 font-medium">Date</th>
                                <th className="py-1.5 text-center text-gray-500 font-medium">Sent</th>
                                <th className="py-1.5 text-center text-gray-500 font-medium">Opened</th>
                                <th className="py-1.5 text-center text-gray-500 font-medium">Clicked</th>
                                <th className="py-1.5 text-center text-gray-500 font-medium">Replied</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const dayAgg = {};
                                for (const r of analytics.daily) {
                                  if (!dayAgg[r.date]) dayAgg[r.date] = {};
                                  dayAgg[r.date][r.activity_type] = r.count;
                                }
                                return Object.entries(dayAgg).sort(([a],[b]) => b.localeCompare(a)).map(([date, acts]) => (
                                  <tr key={date} className="border-b border-gray-100">
                                    <td className="py-1.5 text-gray-600">{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                                    <td className="py-1.5 text-center">{(acts.email_sent || 0) + (acts.sms_sent || 0)}</td>
                                    <td className="py-1.5 text-center">{acts.email_opened || 0}</td>
                                    <td className="py-1.5 text-center">{acts.link_clicked || 0}</td>
                                    <td className="py-1.5 text-center">{acts.email_replied || 0}</td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

        </div>
      </div>
      )}
      </div>

      {/* SETTINGS PANEL — docks under the header, pushes content left (in-flow) */}
      {showSettings && (
        <div className="flex-shrink-0 w-[360px] bg-white border-l border-gray-200 flex flex-col overflow-hidden animate-[slidein_180ms_ease-out]">
          <style>{`@keyframes slidein { from { width: 0; opacity: 0; } to { width: 360px; opacity: 1; } }`}</style>
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Campaign Settings</h3>
            <button onClick={() => setShowSettings(false)} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-6">

              {/* General */}
              <div className="bg-white rounded-lg border shadow-sm p-5 space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">General</h3>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Campaign Name</label>
                  <input type="text" value={campaign.name || ''} onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
                    onBlur={() => updateCampaign({ name: campaign.name })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={campaign.description || ''} onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
                    onBlur={() => updateCampaign({ description: campaign.description })}
                    className="w-full px-3 py-2 text-sm border rounded-lg resize-none" rows={2} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Default Send From</label>
                  <select value={campaign.send_from_type || 'recruiter_outlook'}
                    onChange={(e) => { setCampaign(prev => ({ ...prev, send_from_type: e.target.value })); updateCampaign({ send_from_type: e.target.value }); }}
                    className="w-full px-3 py-2 text-sm border rounded-lg">
                    <option value="recruiter_outlook">Each recruiter's connected Outlook</option>
                    <option value="shared">Shared mailbox / specific address</option>
                  </select>
                </div>
                {campaign.send_from_type === 'shared' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Shared Email Address</label>
                    <input type="email" value={campaign.send_from_email || ''} onChange={(e) => setCampaign(prev => ({ ...prev, send_from_email: e.target.value }))}
                      onBlur={() => updateCampaign({ send_from_email: campaign.send_from_email })}
                      placeholder="recruiting@banyak.group"
                      className="w-full px-3 py-2 text-sm border rounded-lg" />
                  </div>
                )}
              </div>

              {/* Linked Searches */}
              <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Connected Searches</h3>
                <p className="text-xs text-gray-500">Link searches to auto-update pool statuses when outreach is sent.</p>
                {linkedSearches.length > 0 && (
                  <div className="space-y-1">
                    {linkedSearches.map(s => (
                      <div key={s.pipeline_id} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs">
                        <span className="text-gray-800">{s.pipeline_name} <span className="text-gray-400">({s.enrolled_from_search} enrolled)</span></span>
                        <button onClick={() => unlinkSearch(s.pipeline_id)} className="text-red-400 hover:text-red-600 text-[10px]">Unlink</button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input type="text" value={searchLinkQuery} onChange={(e) => {
                      setSearchLinkQuery(e.target.value);
                      if (e.target.value.length >= 2) {
                        fetch(`${API}/api/pipelines?search=${encodeURIComponent(e.target.value)}`, { headers })
                          .then(r => r.ok ? r.json() : []).then(setAllSearches).catch(() => {});
                      }
                    }}
                    placeholder="Search for a pipeline to link..."
                    className="flex-1 px-3 py-1.5 text-xs border rounded-lg" />
                </div>
                {searchLinkQuery.length >= 2 && allSearches.length > 0 && (
                  <div className="border rounded-lg max-h-32 overflow-auto">
                    {allSearches.filter(s => !linkedSearches.some(ls => ls.pipeline_id === s.id)).map(s => (
                      <button key={s.id} onClick={() => linkSearch(s.id)}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0">
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Reply Behavior */}
              <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Reply Behavior</h3>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Continue steps after reply</span><p className="text-gray-400 mt-0.5">If off, sequence pauses when candidate replies</p></div>
                  <button onClick={() => updateSettings({ continue_after_reply: !settings.continue_after_reply })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.continue_after_reply ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.continue_after_reply ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Forward replies to teammates</span><p className="text-gray-400 mt-0.5">If a reply comes to one user, forward to all campaign members</p></div>
                  <button onClick={() => updateSettings({ forward_replies: !settings.forward_replies })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.forward_replies ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.forward_replies ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Send in recipient's timezone</span><p className="text-gray-400 mt-0.5">Deliver emails at the scheduled time in the candidate's local timezone</p></div>
                  <button onClick={() => updateSettings({ recipient_timezone: !settings.recipient_timezone })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.recipient_timezone ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.recipient_timezone ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
              </div>

              {/* Privacy & Tracking */}
              <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Privacy & Tracking</h3>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Hide from other team members</span><p className="text-gray-400 mt-0.5">Only you can see this campaign</p></div>
                  <button onClick={() => updateSettings({ is_private: !settings.is_private })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.is_private ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.is_private ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Track email opens</span><p className="text-gray-400 mt-0.5">Embed tracking pixel to detect when emails are opened</p></div>
                  <button onClick={() => updateSettings({ track_opens: settings.track_opens === false })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.track_opens !== false ? 'bg-emerald-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.track_opens !== false ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Track link clicks</span><p className="text-gray-400 mt-0.5">Redirect links through tracker to measure clicks</p></div>
                  <button onClick={() => updateSettings({ track_clicks: settings.track_clicks === false })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.track_clicks !== false ? 'bg-emerald-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.track_clicks !== false ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
                <label className="flex items-center justify-between py-1.5 text-xs">
                  <div><span className="text-gray-800 font-medium">Include unsubscribe link</span><p className="text-gray-400 mt-0.5">Add a small unsubscribe link at the bottom of outreach emails</p></div>
                  <button onClick={() => updateSettings({ include_unsubscribe: !settings.include_unsubscribe })}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors ${settings.include_unsubscribe ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${settings.include_unsubscribe ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </label>
              </div>

              {/* Email Signature */}
              <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900">Email Signature</h3>
                <p className="text-xs text-gray-500">Your signature is automatically appended to initial outreach emails. Reply follow-ups exclude it by default (configurable per step).</p>
                {signatureLoading ? (
                  <div className="py-4 text-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div></div>
                ) : (
                  <>
                    <EmailBodyEditor value={signature} onChange={setSignature}
                      placeholder={"Best regards,\n{{sender_name}}\n{{sender_title}}, {{firm_name}}"}
                      minHeight={160} />
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-gray-400">This signature is shared across all your campaigns.</p>
                      <button onClick={saveSignature} disabled={signatureSaving}
                        className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                        {signatureSaving ? 'Saving...' : 'Save Signature'}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Danger Zone */}
              <div className="bg-white rounded-lg border border-red-200 shadow-sm p-5">
                <h3 className="text-sm font-semibold text-red-600 mb-2">Danger Zone</h3>
                <button onClick={() => { if (window.confirm('Archive this campaign? Active sequences will be paused.')) updateCampaign({ status: 'archived' }); }}
                  className="text-xs text-red-500 hover:text-red-700 border border-red-200 px-3 py-1.5 rounded hover:bg-red-50">
                  Archive Campaign
                </button>
              </div>
              </div>
            </div>
        )}
      </div>

      <CandidatePreviewPanel
        candidateId={previewCandidateId}
        isOpen={isPreviewOpen}
        onClose={() => { setIsPreviewOpen(false); setPreviewCandidateId(null); }}
        onPrevCandidate={() => {
          const idx = filteredRoster.findIndex(rc => rc.candidate_id === previewCandidateId);
          if (idx > 0) setPreviewCandidateId(filteredRoster[idx - 1].candidate_id);
        }}
        onNextCandidate={() => {
          const idx = filteredRoster.findIndex(rc => rc.candidate_id === previewCandidateId);
          if (idx !== -1 && idx < filteredRoster.length - 1) setPreviewCandidateId(filteredRoster[idx + 1].candidate_id);
        }}
        hasPrev={(() => { const idx = filteredRoster.findIndex(rc => rc.candidate_id === previewCandidateId); return idx > 0; })()}
        hasNext={(() => { const idx = filteredRoster.findIndex(rc => rc.candidate_id === previewCandidateId); return idx !== -1 && idx < filteredRoster.length - 1; })()}
      />
    </AppShell>
  );
};

export default CampaignDetailPage;
